import { z } from 'zod';
import type { ExcelPhraseRow } from './excel-phrase.types';

/** Backend `GET /words/:id/phrase-stats` — recordings of each phrase of a workbook. */

const count = z.number().int().nonnegative();
const statsSchema = z
  .object({
    id: z.string().nullable(),
    local: z.string(),
    fr: z.string(),
    recordings: count,
    contributors: count,
    validated: count,
    pending: count,
    rejected: count,
  })
  .strict();

const envelope = z
  .object({
    error: z.literal(false),
    message: z.literal('PHRASE_RECORDING_STATS_READ'),
    data: z.object({ lang: z.string(), items: z.array(statsSchema) }).strict(),
  })
  .passthrough();

export type PhraseRecordingStats = z.infer<typeof statsSchema>;
export type RecordingSort = 'file' | 'most' | 'least' | 'never';

export function parsePhraseRecordingStats(body: unknown) {
  return envelope.parse(body).data.items;
}

const textKey = (local: string, fr: string) => `${local.trim()}\u0000${fr.trim()}`;

/** Finds the stats of a workbook row: by phrase id, else by its text. */
export function statsIndex(items: PhraseRecordingStats[]) {
  const byId = new Map<string, PhraseRecordingStats>();
  const byText = new Map<string, PhraseRecordingStats>();
  for (const item of items) {
    if (item.id) byId.set(item.id.toLowerCase(), item);
    const key = textKey(item.local, item.fr);
    if (!byText.has(key)) byText.set(key, item);
  }
  return (row: Pick<ExcelPhraseRow, 'id' | 'local' | 'fr'>) =>
    (row.id ? byId.get(row.id.toLowerCase()) : undefined) ?? byText.get(textKey(row.local, row.fr));
}

/** Orders or narrows rows by how much they were recorded; "file" keeps the workbook order. */
export function applyRecordingSort<T>(rows: T[], sort: RecordingSort, recordingsOf: (row: T) => number): T[] {
  if (sort === 'never') return rows.filter((row) => recordingsOf(row) === 0);
  if (sort === 'file') return rows;
  const sign = sort === 'most' ? -1 : 1;
  return [...rows].sort((a, b) => sign * (recordingsOf(a) - recordingsOf(b)));
}
